<template>
    <div class="page-container">
        <!-- 顶部标签栏 (保持不变) -->
        <div class="tabs-container">
            <div v-for="(tab, index) in tabs" :key="index" class="tab-item" :class="{ active: currentTab === index }"
                @click="currentTab = index">
                <span class="tab-text">{{ tab.name }}</span>
                <span v-if="tab.count > 0" class="badge">{{ tab.count }}</span>
                <div v-if="currentTab === index" class="active-line"></div>
            </div>
        </div>

        <div class="order-list">
            <!-- 订单列表循环 -->
            <div class="order-card" v-for="item in orderList" :key="item.id" @click="handleClick(item.id)">
                <div class="card-header">
                    <span class="customer">购买客户：{{ item.customer }}</span>
                    <span class="status" :class="getStatusClass(item.status)">{{ item.status }}</span>
                </div>
                <div class="card-body">
                    <img :src="item.image" class="product-img" alt="Product" />
                    <div class="product-info">
                        <div class="product-title">{{ item.title }}</div>
                        <div class="product-specs">规格：{{ item.specs }}</div>
                        <div class="price-row">
                            <span class="price">¥{{ item.price }}</span>
                            <span class="quantity">x{{ item.quantity }}</span>
                        </div>
                    </div>
                </div>

                <div class="card-footer">
                    <span class="order-no">订单号：{{ item.orderNo }}</span>
                    <div class="total-wrap">
                        <span>实付：</span>
                        <span class="total-price">¥{{ item.realPay }}</span>
                    </div>
                </div>
            </div>

            <!-- === 新增：底部加载触发区 === -->
            <div class="loading-trigger" ref="loadTrigger">
                <div v-if="isLoading" class="loading-state">
                    <span class="spinner"></span> 正在加载中...
                </div>
                <div v-else-if="isFinished" class="finished-state">
                    - 没有更多订单了 -
                </div>
                <div v-else class="idle-state">
                    <!-- 这里的空div是为了占位，让Observer能监测到 -->
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';

// --- 类型定义 ---
interface OrderItem {
    id: number;
    customer: string;
    status: string;
    title: string;
    specs: string;
    price: string;
    quantity: number;
    image: string;
    orderNo: string;
    realPay: string;
}

// 标签栏数据
const tabs = ref([
    { name: '全部', count: 0 },
    { name: '待付款', count: 1 },
    { name: '待发货', count: 0 },
    { name: '待收货', count: 0 },
    { name: '已完成', count: 0 },
    { name: '已退款', count: 0 },
]);

const currentTab = ref(0);

// --- 订单数据与分页状态 ---
const orderList = ref<OrderItem[]>([
    {
        id: 1,
        customer: '张桂芳',
        status: '待发货',
        title: '杞里香化橘红',
        specs: '100ml',
        price: '129.9',
        quantity: 1,
        image: 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80',
        orderNo: '007122231323',
        realPay: '129.9'
    },
    {
        id: 2,
        customer: '张琳琳',
        status: '已完成',
        title: '杞里香化橘红',
        specs: '100ml',
        price: '129.9',
        quantity: 1,
        image: 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80',
        orderNo: '007122231323',
        realPay: '129.9'
    },
    {
        id: 3,
        customer: '张琳琳',
        status: '已完成',
        title: '会员注册',
        specs: '100ml',
        price: '9.9',
        quantity: 1,
        image: 'https://images.unsplash.com/photo-1614680376593-902f74cf0d41?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80',
        orderNo: '007122231323',
        realPay: '9.9'
    }
]);

// === 分页逻辑变量 ===
const isLoading = ref(false); // 是否正在加载
const isFinished = ref(false); // 是否已加载完所有数据
const page = ref(1); // 当前页码
const loadTrigger = ref<HTMLElement | null>(null); // DOM引用
let observer: IntersectionObserver | null = null;

// --- 方法 ---
const getStatusClass = (status: string) => {
    if (status === '待发货') return 'status-orange';
    if (status === '已完成') return 'status-gold';
    return '';
};

const mockFetchData = async (pageNum: number): Promise<OrderItem[]> => {
    return new Promise((resolve) => {
        setTimeout(() => {
            // 模拟每次返回 3 条数据
            const newItems: OrderItem[] = Array.from({ length: 3 }).map((_, i) => ({
                id: Date.now() + i, // 确保key唯一
                customer: pageNum % 2 === 0 ? '李大伟' : '王小红',
                status: i % 2 === 0 ? '已完成' : '待发货',
                title: '追加订单数据 ' + pageNum,
                specs: '100ml',
                price: '129.9',
                quantity: 1,
                image: 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80',
                orderNo: '007122231323',
                realPay: '129.9'
            }));
            resolve(newItems);
        }, 1000); // 模拟网络延迟 1秒
    });
};
const loadMore = async () => {
    if (isLoading.value || isFinished.value) return;
    isLoading.value = true;
    try {
        page.value++;
        const newItems = await mockFetchData(page.value);
        orderList.value.push(...newItems);

        // 模拟数据加载完毕 (这里假设最多加载到第 3 页)
        if (page.value >= 3) {
            isFinished.value = true;
        }
    } catch (error) {
        console.error('加载失败', error);
    } finally {
        isLoading.value = false;
    }
};

// ===点击跳转详情 ===
import { useRouter ,useRoute } from 'vue-router'
const router = useRouter()
const handleClick = (id: number) => {
    router.push({path: '/orderdetails', query: { id }});
};

// ===生命周期监听 ===
onMounted(() => {
    currentTab.value = Number(useRoute().query.order_type || 0);
    observer = new IntersectionObserver((entries) => {
        // 当底部元素进入视口时
        if (entries[0].isIntersecting) {
            loadMore();
        }
    }, {
        threshold: 0.1, // 元素露出 10% 时触发
        rootMargin: '50px' // 距离底部还有 50px 时提前触发
    });
    if (loadTrigger.value) {
        observer.observe(loadTrigger.value);
    }
});

onUnmounted(() => {
    // 销毁监听器
    if (observer) observer.disconnect();
});
</script>

<style lang="less" scoped>
// 变量定义
@color-primary: #333;
@color-secondary: #999;
@color-orange: #FF7123;
@color-gold: #DCBB89;
@color-line: #eee;

.page-container {
    min-height: 100vh;
    font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Helvetica Neue", Arial, sans-serif;
    color: @color-primary;
}

.tabs-container {
    display: flex;
    background: #fff;
    overflow-x: auto;
    padding: 0 10px;
    border-bottom: 1px solid transparent;
    position: fixed;
    top: 0; // 确保固定在顶部
    left: 0;
    width: 100%; // 确保占满宽度
    z-index: 10; // 确保在内容之上

    // 隐藏滚动条
    &::-webkit-scrollbar {
        display: none;
    }

    .tab-item {
        position: relative;
        padding: 13px 8px;
        white-space: nowrap;
        font-size: 15px;
        color: #999;
        cursor: pointer;

        &.active {
            color: #DCBB89;
            font-weight: 600;
        }

        .badge {
            position: absolute;
            top: 4px;
            right: 0px;
            background-color: @color-orange;
            color: #fff;
            font-size: 10px;
            height: 14px;
            min-width: 14px;
            line-height: 14px;
            text-align: center;
            border-radius: 7px;
            padding: 0 3px;
        }

        .active-line {
            position: absolute;
            bottom: 8px;
            left: 50%;
            transform: translateX(-50%);
            width: 20px;
            height: 3px;
            background-color: #dcb36d;
            border-radius: 2px;
        }
    }
}

// 3. 订单列表样式
.order-list {
    padding: 12px;
    margin-top: 50px; // 留出 Header 的位置

    .order-card {
        background: #fff;
        border-radius: 8px;
        padding: 15px;
        margin-bottom: 10px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);

        // 头部：客户与状态
        .card-header {
            display: flex;
            justify-content: space-between;
            margin-bottom: 15px;
            font-size: 15px;

            .customer {
                color: #333;
            }

            .status {
                font-weight: 500;
            }

            .status-orange {
                color: @color-orange;
            }

            .status-gold {
                color: @color-gold;
            }
        }

        .card-body {
            display: flex;
            margin-bottom: 15px;

            .product-img {
                width: 80px;
                height: 80px;
                border-radius: 8px;
                object-fit: cover;
                margin-right: 12px;
                background-color: #eee;
            }

            .product-info {
                flex: 1;
                display: flex;
                flex-direction: column;
                justify-content: space-between;

                .product-title {
                    font-size: 15px;
                    color: #333;
                    font-weight: 500;
                    line-height: 1.4;
                }

                .product-specs {
                    font-size: 12px;
                    color: @color-secondary;
                    margin-top: 4px;
                }

                .price-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-top: 8px;

                    .price {
                        font-size: 16px;
                        color: @color-orange;
                        font-weight: 600;
                    }

                    .quantity {
                        font-size: 14px;
                        color: @color-secondary;
                    }
                }
            }
        }

        .card-footer {
            border-top: 1px solid #f7f7f7;
            padding-top: 12px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 13px;
            color: #666;

            .order-no {
                color: #888;
            }

            .total-wrap {
                display: flex;
                align-items: center;

                .total-price {
                    color: #333;
                    font-size: 15px;
                    font-weight: 500;
                }
            }
        }
    }

    // === 新增：底部 Loading 区域样式 ===
    .loading-trigger {
        text-align: center;
        padding: 15px 0;
        color: #999;
        font-size: 13px;
        min-height: 40px; 
        display: flex;
        justify-content: center;
        align-items: center;

        .loading-state {
            display: flex;
            align-items: center;

            // 加载圈圈动画
            .spinner {
                width: 14px;
                height: 14px;
                border: 2px solid #ccc;
                border-top-color: @color-orange;
                border-radius: 50%;
                margin-right: 8px;
                animation: spin 0.8s linear infinite;
            }
        }
        
        .finished-state {
            color: #ccc;
        }
    }
}

// 旋转动画关键帧
@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}
</style>